/**
 * Miro — the reference semantic connector profile.
 *
 * The richest spatial dialect we integrate with: explicit edges (connectors),
 * containment (frames), a visual language (16 sticky colors, shapes, tags) and
 * free canvas layout. Reference tables:
 * docs/9-exploration/semantic-connectors/03-PROFILE-miro-reference.md.
 *
 * The default maps below are profile-shipped intelligent defaults; workspace
 * semantic maps (connector_configs.field_mappings v3) override them.
 */

import type { ConnectorProfile } from '../profileTypes';
import type { RelationshipCueRule, TypeMapRule } from '../../fieldMappingRegistry';

export const MIRO_PROFILE: ConnectorProfile = {
  connectorType: 'miro',
  profileVersion: 1,

  nativeObjectTypes: [
    'sticky_note', 'card', 'shape', 'text', 'frame', 'connector', 'tag', 'image', 'document', 'comment',
  ],
  relationshipModel: 'explicit_edges',
  visualChannels: [
    'fill_color', 'shape_color', 'shape',
    'line_color', 'line_type', 'arrow_color', 'arrow_shape', 'line_style',
  ],
  spatialModel: 'canvas',
  acquisition: 'api',

  fidelity: {
    content: {
      export: 'full',
      import: 'full',
      notes: 'Long descriptions export as cards (stickies are short-form). Comments and images survive as annotations/attachments.',
    },
    structure: {
      export: 'partial',
      import: 'partial',
      notes: 'Frames carry grouping and the excursion scope. Fine z-order/adjacency is not preserved. Miro frames cannot carry a fill color.',
    },
    relationships: {
      export: 'partial',
      import: 'partial',
      notes: 'Connectors carry direction, caption, and line style — dependency, prerequisite, elaboration, contains and related survive. Package atomicity and Exclusive have no native Miro form; they export as tagged frames (convention, not enforcement).',
    },
    semantics: {
      export: 'partial',
      import: 'partial',
      notes: 'Category rides fill color, altitude rides mapped channels; meaning not encoded in a mapped channel does not survive. If a collaborator deletes a caption, the edge returns at reduced confidence for review.',
    },
  },

  identity: { stableExternalIds: true, deepLinks: true, appMetadata: false },
  continuity: { webhooks: true, comments: true },
};

// ── Profile-shipped default maps (workspace v3 rules override) ────────────────

/** Native type routing defaults. */
export const MIRO_DEFAULT_TYPE_MAP: TypeMapRule[] = [
  { nativeType: 'sticky_note', target: 'item' },
  { nativeType: 'card', target: 'item' },
  { nativeType: 'shape', target: 'item' },
  { nativeType: 'frame', target: 'region' },
  { nativeType: 'text', target: 'annotation' },
  { nativeType: 'image', target: 'annotation' },
  { nativeType: 'connector', target: 'ignore' }, // handled by the relationship map
  { nativeType: 'tag', target: 'ignore' },
];

/**
 * Dialect cue → canonical relationship defaults. Caption cues include the
 * canonical type names themselves so our own exports round-trip losslessly
 * even if a workspace has no custom rules. Precedence (caption > symbol >
 * link_type > edge_kind > containment) is enforced by resolveRelationshipCue.
 */
export const MIRO_DEFAULT_RELATIONSHIP_MAP: RelationshipCueRule[] = [
  // Round-trip self-captions (what our export writes on connectors)
  { cue: { kind: 'caption', value: 'dependency' }, canonicalType: 'dependency', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'prerequisite' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'elaboration' }, canonicalType: 'elaboration', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'contains' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'breakdown' }, canonicalType: 'breakdown', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'aggregation' }, canonicalType: 'aggregation', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'related' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 1, basis: 'explicit' },
  // Human phrasing (mirrors the whiteboard SYMBOL_RULES vocabulary)
  { cue: { kind: 'caption', value: 'blocks' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'needs' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'waiting on' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'elaborates' }, canonicalType: 'elaboration', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'caption', value: 'detail of' }, canonicalType: 'elaboration', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'symbol', value: '⊃' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 0.9, basis: 'symbol' },
  // Line style fallbacks (uncaptioned connectors)
  { cue: { kind: 'edge_kind', value: 'normal' }, canonicalType: 'dependency', subtype: 'soft', directionality: 'directed', defaultConfidence: 0.8, basis: 'visual_language' },
  { cue: { kind: 'edge_kind', value: 'dashed' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 0.7, basis: 'visual_language' },
  { cue: { kind: 'edge_kind', value: 'dotted' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 0.6, basis: 'visual_language' },
  // Containment (sticky dragged into a non-scope frame) — spatial, proposal-only
  { cue: { kind: 'containment', value: 'frame' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 0.6, basis: 'spatial' },
];

/**
 * Category → Miro sticky fill color for export (reverse of the import
 * DEFAULT_COLOR_TO_CATEGORY in whiteboardImportService/miroService). Named
 * palette colors only — Miro sticky notes reject arbitrary hex.
 */
export const MIRO_CATEGORY_TO_COLOR: Record<string, string> = {
  risk: 'red',
  problem: 'orange',
  opportunity: 'green',
  solution: 'blue',
  investment: 'violet',
  commitment: 'dark_blue',
};

export const MIRO_DEFAULT_STICKY_COLOR = 'yellow';

/** Canonical relationship → connector stroke style for export. */
export const MIRO_EDGE_STROKE: Record<string, 'normal' | 'dashed' | 'dotted'> = {
  dependency: 'normal',
  prerequisite: 'normal',
  elaboration: 'normal',
  contains: 'normal',
  breakdown: 'normal',
  aggregation: 'normal',
  related: 'dashed',
};

/** Description length beyond which an item exports as a card, not a sticky. */
export const MIRO_CARD_DESCRIPTION_THRESHOLD = 120;
